import { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { BrowserRouter, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { OfflineSyncBadge } from "@/components/OfflineSyncBadge";
import { HealthStatusBanner } from "@/components/HealthStatusBanner";
import { UpdatePrompt } from "@/components/UpdatePrompt";
import BeltLoader from "@/components/BeltLoader";
import { DevMockProvider } from "@/contexts/DevMockContext";
import { initOfflineSync } from "@/lib/offlineSync";
import { logError, logWarn } from "@/lib/monitoring";
import { supabase } from "@/integrations/supabase/client";

// Initialize offline sync listener
initOfflineSync();

// Route groups
import { publicRoutes } from "@/routes/publicRoutes";
import { devRoutes } from "@/routes/devRoutes";
import { superAdminRoutes } from "@/routes/superAdminRoutes";
import { adminRoutes } from "@/routes/adminRoutes";
import { professorRoutes } from "@/routes/professorRoutes";
import { alunoRoutes } from "@/routes/alunoRoutes";

/**
 * Global query/mutation error handler.
 * - 401/403 (PGRST301): token expired → auto-signout
 * - 429: rate limited → logged with warning
 * - 5xx: server error → toast + monitoring log
 */
function handleGlobalQueryError(error: Error) {
  const msg = error.message ?? "";
  const code = (error as any)?.code ?? "";

  // JWT expired or invalid — force sign out
  if (
    msg.includes("JWT") ||
    msg.includes("token") ||
    code === "PGRST301" ||
    msg.includes("401") ||
    msg.includes("Invalid login")
  ) {
    logWarn("Session expired, auto-signout triggered", { error: msg });
    supabase.auth.signOut().then(() => {
      window.location.href = "/";
    });
    return;
  }

  // Rate limited
  if (msg.includes("429") || msg.includes("rate limit") || msg.includes("too many")) {
    logWarn("Rate limited by server", { error: msg });
    return;
  }

  // Server errors — log to monitoring
  logError("Global query error", error, { route: window.location.pathname });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const msg = (error as Error)?.message ?? "";
        // Don't retry auth errors or rate limits
        if (msg.includes("JWT") || msg.includes("401") || msg.includes("429")) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 1000 * 60 * 2, // 2 min
      gcTime: 1000 * 60 * 5, // 5 min (keep stale data briefly for back-nav)
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
      onError: (error: Error) => {
        handleGlobalQueryError(error);
        import("sonner").then(({ toast }) => {
          toast.error("Erro na operação", {
            description: error.message || "Tente novamente em instantes.",
          });
        });
      },
    },
  },
});

// Also handle query errors globally via the QueryCache
queryClient.getQueryCache().config.onError = handleGlobalQueryError;

const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <BeltLoader />
  </div>
);

/** Listens for ErrorBoundary reset events and clears React Query cache */
function QueryResetListener() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const handler = () => {
      queryClient.resetQueries();
    };
    window.addEventListener("error-boundary-reset", handler);
    return () => window.removeEventListener("error-boundary-reset", handler);
  }, [queryClient]);
  return null;
}

/** Intercepts PASSWORD_RECOVERY auth event and redirects to /redefinir-senha */
function RecoveryRedirectGuard() {
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        const current = window.location.pathname;
        if (current !== "/redefinir-senha") {
          window.location.replace("/redefinir-senha");
        }
      }
    });
    return () => subscription.unsubscribe();
  }, []);
  return null;
}

/** Keeps the <html lang="..."> attribute in sync with i18next language */
function LangSync() {
  const { i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = i18n.language?.split("-")[0] || "pt";
  }, [i18n.language]);
  return null;
}

const App = () => (
  <ErrorBoundary>
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} disableTransitionOnChange>
    <QueryClientProvider client={queryClient}>
      <QueryResetListener />
      <RecoveryRedirectGuard />
      <LangSync />
      <BrowserRouter>
        <TooltipProvider>
        <OfflineIndicator />
        <HealthStatusBanner />
        <OfflineSyncBadge />
        <UpdatePrompt />
        <Toaster />
        <Sonner />
        <DevMockProvider>
        <Suspense fallback={<LazyFallback />}>
        <Routes>
          {publicRoutes}
          {devRoutes}
          {superAdminRoutes}
          {adminRoutes}
          {professorRoutes}
          {alunoRoutes}
        </Routes>
        </Suspense>
        </DevMockProvider>
        </TooltipProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </ThemeProvider>
  </ErrorBoundary>
);

export default App;
